
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, X, Filter, ArrowUpDown, Star, Leaf, 
  MapPin, Zap, ChevronDown, RefreshCw, Grid, 
  BookOpen, Check, ChevronRight, ChevronLeft,
  Sun, Snowflake, CloudRain, Tractor, ShoppingCart, Minus, Plus, ChevronUp
} from 'lucide-react';
import { useProduct } from '../services/ProductContext';
import { useCart } from '../services/CartContext';
import { useImage } from '../services/ImageContext';
import { ProductCard } from '../components/ui/ProductCard';
import { ProductCardSkeleton } from '../components/ui/Skeleton';
import { Product } from '../types';
import { useFarmer } from '../services/FarmerContext';

// --- MOBILE 3D CAROUSEL COMPONENT ---
const Mobile3DCarousel: React.FC<{ 
  title: string; 
  subtitle?: string;
  products: Product[]; 
  onSeeAll: () => void;
  bgColor?: string;
}> = ({ title, subtitle, products, onSeeAll, bgColor = 'bg-white' }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // 3D Scroll Effect Logic
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const containerCenter = container.scrollLeft + container.clientWidth / 2;
      
      Array.from(container.children).forEach((child) => {
        const element = child as HTMLElement;
        const innerWrapper = element.querySelector('.card-wrapper') as HTMLElement;
        
        if (innerWrapper) {
            const childCenter = element.offsetLeft + element.offsetWidth / 2;
            const distance = Math.abs(containerCenter - childCenter);
            const maxDistance = container.clientWidth / 1.5; // Sensitivity
            
            // Calculate normalized distance (0 = center, 1 = edge)
            let normalized = 0;
            if (maxDistance > 0) {
               normalized = Math.min(distance / maxDistance, 1);
            }
            
            // 3D Transforms
            const scale = 1 - (normalized * 0.15); // Scale down to 0.85 at edges
            const opacity = 1 - (normalized * 0.4); // Fade out to 0.6
            const rotateY = (childCenter - containerCenter) / 25; // Gentler rotation
            
            innerWrapper.style.transform = `perspective(1000px) rotateY(${rotateY}deg) scale(${scale})`;
            innerWrapper.style.opacity = `${opacity}`;
            innerWrapper.style.zIndex = `${Math.round((1 - normalized) * 10)}`;
        }
      });
    };

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('touchmove', handleScroll); // Ensure touch updates
    
    // Initial calculation after a short delay to ensure layout is ready
    setTimeout(handleScroll, 100);
    
    return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('touchmove', handleScroll);
    };
  }, [products]);

  const displayProducts = products.slice(0, 8); 
  if (displayProducts.length === 0) return null;

  return (
    <div className={`py-8 border-b border-gray-50 last:border-0 ${bgColor} md:hidden overflow-hidden min-h-[380px]`}>
      <div className="flex justify-between items-end px-6 mb-4">
        <div>
            <h3 className="text-xl font-extrabold text-gray-900 leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 font-bold mt-1 uppercase tracking-wide opacity-80">{subtitle}</p>}
        </div>
        <button onClick={onSeeAll} className="text-xs font-bold text-leaf-700 flex items-center gap-1 bg-leaf-50/80 px-3 py-1.5 rounded-full shadow-sm hover:bg-leaf-100 transition">
          View All <ChevronRight size={14} />
        </button>
      </div>
      
      <div className="relative group">
        <div 
            ref={scrollRef}
            className="flex overflow-x-auto gap-0 px-[10vw] pb-10 pt-4 scrollbar-hide snap-x snap-mandatory scroll-smooth"
            style={{ paddingLeft: '12.5vw', paddingRight: '12.5vw' }}
        >
            {displayProducts.map((product, idx) => {
                return (
                    <div 
                        key={product.id} 
                        className="min-w-[75vw] snap-center flex justify-center py-2"
                    >
                        {/* 3D Card Wrapper with initial styles to prevent invisibility before JS runs */}
                        <div 
                            className="card-wrapper w-full bg-white rounded-3xl shadow-xl h-auto" 
                            style={{ 
                                transformStyle: 'preserve-3d',
                                transition: 'transform 0.1s ease-out, opacity 0.1s ease-out',
                                opacity: 1, // Ensure visible by default
                                transform: 'scale(1)' 
                            }}
                        >
                             <ProductCard product={product} /> 
                        </div>
                    </div>
                );
            })}
        </div>
      </div>
    </div>
  );
};

// --- COLLAPSIBLE FILTER SECTION COMPONENT ---
const FilterSection: React.FC<{ 
    title: string; 
    icon?: React.ElementType; 
    children: React.ReactNode; 
    defaultOpen?: boolean 
}> = ({ title, icon: Icon, children, defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mb-4 transition-all">
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="w-full flex items-center justify-between font-bold text-gray-900 text-sm uppercase tracking-wide mb-2 group"
            >
                <div className="flex items-center gap-2">
                    {Icon && <Icon size={16} className="text-leaf-600"/>} {title}
                </div>
                {isOpen ? <ChevronUp size={16} className="text-gray-400 group-hover:text-leaf-600"/> : <ChevronDown size={16} className="text-gray-400 group-hover:text-leaf-600"/>}
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                {children}
            </div>
        </div>
    );
};

export const Shop: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { products, loading } = useProduct();
  const { farmers } = useFarmer();
  const { getProductImage } = useImage();
  const { addToCart } = useCart();
  
  // URL Params
  const query = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || 'All';

  // --- LOCAL STATE ---
  const maxProductPrice = useMemo(() => products.length > 0 ? Math.max(...products.map(p => p.price), 1000) : 1000, [products]);
  
  // Price Range State
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(10000); 
  
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [sortOption, setSortOption] = useState('featured');
  const [viewMode, setViewMode] = useState<'grid' | 'journal'>('grid'); 
  const [farmerSearch, setFarmerSearch] = useState('');
  
  // Advanced Filters State
  const [filters, setFilters] = useState<{
    organic: boolean;
    local: boolean;
    onSale: boolean;
    inStock: boolean;
    rating4Plus: boolean;
    farmers: Set<string>;
    subCategories: Set<string>;
  }>({
    organic: false,
    local: false,
    onSale: false,
    inStock: false,
    rating4Plus: false,
    farmers: new Set(),
    subCategories: new Set()
  });

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Device Check
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 768);

  // Available Data for Filters
  const availableFarmers = useMemo(() => {
      const sellerIds = new Set(products.map(p => p.sellerId).filter(Boolean));
      let filteredFarmers = farmers.filter(f => sellerIds.has(f.id));
      if (farmerSearch) {
          filteredFarmers = filteredFarmers.filter(f => f.farmName.toLowerCase().includes(farmerSearch.toLowerCase()) || f.name.toLowerCase().includes(farmerSearch.toLowerCase()));
      }
      return filteredFarmers;
  }, [products, farmerSearch, farmers]);

  const availableSubCategories = useMemo(() => {
      if (selectedCategory === 'Vegetables') {
          return ['Root Veg', 'Fruit Veg', 'Leafy', 'Bulb', 'Beans/Legumes', 'Flower Veg'];
      }
      if (selectedCategory === 'Fruits') {
          return ['Tropical', 'Citrus', 'Stone Fruit', 'Melon', 'Berry', 'Exotic'];
      }
      return [];
  }, [selectedCategory]);

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync state with URL param changes
  useEffect(() => {
    setSelectedCategory(categoryParam);
  }, [categoryParam]);

  useEffect(() => {
    // Only set price range if it hasn't been interacted with (or is default high)
    // and we have a valid max price from products
    if (maxProductPrice > 0 && maxPrice === 10000) {
        setMaxPrice(maxProductPrice);
    }
  }, [maxProductPrice]);

  const getProductDetails = (product: Product) => {
    const farmer = farmers.find((item) => item.id === product.sellerId);
    return {
      origin: product.origin || farmer?.location || 'Origin not specified',
      harvestTime: product.harvestTime || 'Harvest time not specified',
      benefits: product.nutritionHighlights && product.nutritionHighlights.length > 0
        ? product.nutritionHighlights.slice(0, 2)
        : [],
      rating: product.rating
    };
  };

  // --- SEASONAL LOGIC ---
  const seasonalBestSellers = useMemo(() => {
    const month = new Date().getMonth(); // 0-11
    let keywords: string[] = [];
    let seasonName = "";
    let seasonIcon = Sun;
    let seasonColor = "text-orange-500";

    if (month >= 2 && month <= 5) { // Mar - Jun (Summer)
        keywords = ['Mango', 'Melon', 'Cucumber', 'Lychee', 'Watermelon'];
        seasonName = "Summer Specials";
        seasonIcon = Sun;
        seasonColor = "text-yellow-500";
    } else if (month >= 6 && month <= 9) { // Jul - Oct (Monsoon)
        keywords = ['Corn', 'Gourd', 'Pumpkin', 'Guava', 'Jamun'];
        seasonName = "Monsoon Harvest";
        seasonIcon = CloudRain;
        seasonColor = "text-blue-500";
    } else { // Nov - Feb (Winter)
        keywords = ['Carrot', 'Peas', 'Orange', 'Spinach', 'Mustard', 'Radish', 'Strawberry', 'Berry'];
        seasonName = "Winter Wonders";
        seasonIcon = Snowflake;
        seasonColor = "text-blue-400";
    }

    const seasonal = products.filter(p => 
        keywords.some(k => p.name.en.includes(k) || p.category.includes(k)) && p.rating >= 4.0
    ).sort((a,b) => b.rating - a.rating).slice(0, 4);

    return { products: seasonal, name: seasonName, icon: seasonIcon, color: seasonColor };
  }, [products]);

  // --- DERIVED DATA ---
  const categories = useMemo(() => {
    const cats = products.reduce((acc, product) => {
      acc[product.category] = (acc[product.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return cats;
  }, [products]);

  // Default View Condition
  const isDefaultView = useMemo(() => {
    return selectedCategory === 'All' && 
           !query && 
           (maxPrice === maxProductPrice || maxProductPrice === 0 || maxPrice === 10000) && minPrice === 0 &&
           !Object.values(filters).some(val => val === true || (val instanceof Set && val.size > 0));
  }, [selectedCategory, query, minPrice, maxPrice, filters, maxProductPrice]);

  // Filtered Products for Grid View
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      if (query) {
        const lowerQ = query.toLowerCase();
        const matchName = product.name.en.toLowerCase().includes(lowerQ) || 
                          product.name.hi.toLowerCase().includes(lowerQ) ||
                          product.name.bn.toLowerCase().includes(lowerQ);
        const matchCat = product.category.toLowerCase().includes(lowerQ);
        if (!matchName && !matchCat) return false;
      }

      // Main Category Logic
      if (selectedCategory !== 'All') {
         if (selectedCategory === 'Fruits' && !['Fruit', 'Mango', 'Banana', 'Apple', 'Citrus', 'Melon', 'Grapes', 'Berry', 'Stone Fruit', 'Imported Fruit', 'Exotic', 'Tropical'].includes(product.category)) return false;
         else if (selectedCategory === 'Vegetables' && !['Fruit Veg', 'Root Veg', 'Bulb', 'Other Veg', 'Beans/Legumes', 'Flower Veg', 'Leafy'].includes(product.category)) return false;
         else if (!['Fruits', 'Vegetables', 'All'].includes(selectedCategory) && product.category !== selectedCategory) return false;
      }

      // Filter by Price Range
      if (product.price < minPrice || product.price > maxPrice) return false;

      // Checkbox Filters
      if (filters.organic && !product.isOrganic) return false;
      if (filters.local && !product.isLocal) return false;
      if (filters.inStock && !product.inStock) return false;
      if (filters.onSale && !product.oldPrice) return false;
      if (filters.rating4Plus && product.rating < 4) return false;

      // Farmer Filter
      if (filters.farmers.size > 0 && (!product.sellerId || !filters.farmers.has(product.sellerId))) return false;

      // Sub-Category Filter
      if (filters.subCategories.size > 0 && !filters.subCategories.has(product.category)) return false;

      return true;
    }).sort((a, b) => {
      switch (sortOption) {
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'rating': return b.rating - a.rating;
        case 'name': return a.name.en.localeCompare(b.name.en);
        default: return 0;
      }
    });
  }, [products, query, selectedCategory, minPrice, maxPrice, filters, sortOption]);

  // --- CURATED COLLECTIONS ---
  const curatedCollections = useMemo(() => {
    return {
      fruits: products.filter(p => ['Fruit', 'Mango', 'Banana', 'Apple', 'Citrus', 'Melon', 'Grapes', 'Berry', 'Stone Fruit', 'Imported Fruit', 'Exotic'].includes(p.category)),
      vegetables: products.filter(p => ['Fruit Veg', 'Root Veg', 'Bulb', 'Other Veg', 'Beans/Legumes', 'Flower Veg'].includes(p.category)),
      leafy: products.filter(p => p.category === 'Leafy'),
      exotic: products.filter(p => p.category === 'Exotic' || p.category === 'Imported Fruit'),
      deals: products.filter(p => p.oldPrice && p.oldPrice > p.price).sort((a,b) => (b.oldPrice! - b.price) - (a.oldPrice! - a.price)),
    };
  }, [products]);

  // Pagination Logic
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewMode === 'grid' ? 12 : 8; 
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const currentProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [query, selectedCategory, minPrice, maxPrice, filters, sortOption, viewMode]);

  // --- HANDLERS ---
  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    const newParams = new URLSearchParams(searchParams);
    if (cat === 'All') newParams.delete('category');
    else newParams.set('category', cat);
    setSearchParams(newParams);
    setIsMobileFilterOpen(false);
    setFilters(prev => ({...prev, subCategories: new Set()}));
  };

  const toggleFilter = (key: keyof typeof filters) => {
    setFilters(prev => {
        const val = prev[key];
        if (typeof val === 'boolean') {
            return { ...prev, [key]: !val };
        }
        return prev;
    });
  };

  const toggleSetFilter = (key: 'farmers' | 'subCategories', value: string) => {
      setFilters(prev => {
          const newSet = new Set(prev[key]);
          if (newSet.has(value)) newSet.delete(value);
          else newSet.add(value);
          return { ...prev, [key]: newSet };
      });
  };

  const clearFilters = () => {
    setMinPrice(0);
    setMaxPrice(maxProductPrice);
    setFilters({ organic: false, local: false, onSale: false, inStock: false, rating4Plus: false, farmers: new Set(), subCategories: new Set() });
    setSelectedCategory('All');
    setSearchParams({});
    setSortOption('featured');
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const activeFilterCount = [
    selectedCategory !== 'All',
    minPrice > 0 || (maxPrice < maxProductPrice && maxPrice !== 10000),
    filters.organic, filters.local, filters.onSale, filters.inStock, filters.rating4Plus,
    filters.farmers.size > 0, filters.subCategories.size > 0
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#fdfbf7] font-sans text-gray-800">
      
      {/* 1. HERO SLIDER */}
      <div className="relative h-[280px] md:h-[400px] overflow-hidden flex items-center justify-center">
         <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1610348725531-843dff563e2c?auto=format&fit=crop&w=1600&q=80" 
              alt="Hero" 
              className="w-full h-full object-cover brightness-[0.6]"
            />
         </div>
         <div className="relative z-10 text-center text-white px-4">
            <div className="inline-flex items-center gap-2 border border-white/30 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
               <BookOpen size={14} /> The Market • Vol. 12
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-medium mb-3 leading-tight">
               Nature's <span className="italic font-light text-green-300">Inventory</span>
            </h1>
            <p className="text-sm md:text-lg text-white/90 max-w-lg mx-auto font-light leading-relaxed">
               Explore our curated selection of organic produce, harvested daily and delivered fresh to your doorstep.
            </p>
         </div>
      </div>

      {/* 2. STICKY CATEGORY NAV */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm transition-all duration-300">
        <div className="container mx-auto px-4 overflow-x-auto scrollbar-hide">
          <div className="flex justify-start md:justify-center min-w-max gap-8">
            {['All', 'Fruits', 'Vegetables', 'Leafy', 'Exotic'].map(cat => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`py-4 text-sm font-bold uppercase tracking-widest border-b-2 transition-all ${
                  selectedCategory === cat 
                    ? 'border-leaf-600 text-leaf-800' 
                    : 'border-transparent text-gray-400 hover:text-gray-900'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div className="container mx-auto px-0 md:px-4 py-8">
        
        {/* LOADER STATE */}
        {loading && products.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 px-4 md:px-0">
               {[1,2,3,4,5,6,7,8].map(i => <ProductCardSkeleton key={i} />)}
            </div>
        ) : (
            <>
                {/* MOBILE VIEW: CAROUSELS */}
                {!isLargeScreen && isDefaultView ? (
                  <div className="space-y-2 pb-20">
                     
                     {/* Search Bar */}
                     <div className="px-4 mb-6 relative z-20">
                       <div className="relative">
                         <input 
                           type="text" 
                           value={query}
                           onChange={(e) => { 
                               setSearchParams({ ...Object.fromEntries(searchParams), q: e.target.value });
                               setShowSuggestions(true);
                           }}
                           onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                           placeholder="Search items..." 
                           className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-leaf-500 shadow-sm"
                         />
                         <Search className="absolute left-3 top-3 text-gray-400" size={18}/>
                       </div>
                     </div>

                     {/* Seasonal Best Sellers (Mobile) */}
                     {seasonalBestSellers.products.length > 0 && (
                        <Mobile3DCarousel 
                            title={seasonalBestSellers.name} 
                            subtitle="In Season Now • Top Rated" 
                            products={seasonalBestSellers.products} 
                            onSeeAll={() => handleCategoryChange('All')} 
                            bgColor="bg-gradient-to-r from-yellow-50 to-orange-50"
                        />
                     )}

                     <Mobile3DCarousel 
                        title="Orchard Fresh Fruits" 
                        subtitle="Sweetness from the trees" 
                        products={curatedCollections.fruits} 
                        onSeeAll={() => handleCategoryChange('Fruits')} 
                     />

                     <Mobile3DCarousel 
                        title="Daily Farm Veggies" 
                        subtitle="Harvested at dawn" 
                        products={curatedCollections.vegetables} 
                        onSeeAll={() => handleCategoryChange('Vegetables')} 
                     />

                     <Mobile3DCarousel 
                        title="Leafy Greens" 
                        subtitle="Iron-packed foliage" 
                        products={curatedCollections.leafy} 
                        onSeeAll={() => handleCategoryChange('Leafy')} 
                        bgColor="bg-green-50/50"
                     />

                     <Mobile3DCarousel 
                        title="Exotic & Imported" 
                        subtitle="Rare finds globally" 
                        products={curatedCollections.exotic} 
                        onSeeAll={() => handleCategoryChange('Exotic')} 
                     />

                     <Mobile3DCarousel 
                        title="⚡ Deals of the Day" 
                        subtitle="Limited time offers" 
                        products={curatedCollections.deals} 
                        onSeeAll={() => setFilters(f => ({...f, onSale: true}))} 
                        bgColor="bg-orange-50/50"
                     />
                  </div>
                ) : (
                  /* TABLET/DESKTOP VIEW */
                  <div className="flex flex-col lg:flex-row gap-10 px-4 md:px-0">
                    {/* SIDEBAR FILTER & MAIN GRID CONTENT */}
                    <aside className="hidden lg:block w-72 flex-shrink-0 space-y-4 sticky top-32 h-fit z-20">
                      
                      {/* Search */}
                      <div className="relative mb-4">
                         <Search className="absolute left-4 top-3.5 text-gray-400" size={18}/>
                         <input 
                           type="text" 
                           value={query}
                           onChange={(e) => { 
                               setSearchParams({ ...Object.fromEntries(searchParams), q: e.target.value });
                               setShowSuggestions(true);
                           }}
                           onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                           placeholder="Search catalogue..." 
                           className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-leaf-500 focus:ring-4 focus:ring-leaf-500/10 transition shadow-sm"
                         />
                      </div>

                      {/* Filter Sections */}
                      <FilterSection title="Price Range" icon={Zap}>
                          <div className="px-2 pb-2">
                              <div className="flex items-center justify-between mb-4 gap-2">
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 flex items-center w-full">
                                      <span className="text-gray-400 text-xs font-bold mr-1">₹</span>
                                      <input 
                                        type="number" 
                                        min="0" max={maxPrice} 
                                        value={minPrice} 
                                        onChange={(e) => setMinPrice(Number(e.target.value))} 
                                        className="w-full bg-transparent text-sm font-bold text-gray-800 outline-none"
                                        placeholder="Min"
                                      />
                                  </div>
                                  <span className="text-gray-400 font-bold">-</span>
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 flex items-center w-full">
                                      <span className="text-gray-400 text-xs font-bold mr-1">₹</span>
                                      <input 
                                        type="number" 
                                        min={minPrice} max={maxProductPrice} 
                                        value={maxPrice} 
                                        onChange={(e) => setMaxPrice(Number(e.target.value))} 
                                        className="w-full bg-transparent text-sm font-bold text-gray-800 outline-none"
                                        placeholder="Max"
                                      />
                                  </div>
                              </div>
                              <input 
                                type="range" 
                                min="0" 
                                max={maxProductPrice} 
                                value={maxPrice} 
                                onChange={(e) => setMaxPrice(Number(e.target.value))} 
                                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none accent-leaf-600 cursor-pointer" 
                              />
                              <div className="flex justify-between mt-1 text-[10px] text-gray-400 font-bold">
                                  <span>₹0</span>
                                  <span>₹{maxProductPrice}</span>
                              </div>
                          </div>
                      </FilterSection>

                      <FilterSection title="Categories" icon={Grid}>
                          <div className="flex flex-wrap gap-2">
                            {['All', 'Fruits', 'Vegetables', 'Leafy', 'Exotic'].map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => handleCategoryChange(cat)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                                        selectedCategory === cat 
                                        ? 'bg-leaf-50 text-leaf-700 border-leaf-200' 
                                        : 'bg-white text-gray-500 border-gray-100 hover:border-gray-200'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                          </div>
                      </FilterSection>

                      {availableSubCategories.length > 0 && (
                          <FilterSection title="Sub-Category" icon={Leaf}>
                                <div className="space-y-1 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {availableSubCategories.map(sub => (
                                        <label key={sub} className="flex items-center gap-3 cursor-pointer group hover:bg-gray-50 p-2 rounded-lg transition -mx-2">
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition flex-shrink-0 ${filters.subCategories.has(sub) ? 'bg-leaf-600 border-leaf-600' : 'border-gray-300'}`}>
                                                {filters.subCategories.has(sub) && <Check size={10} className="text-white" />}
                                            </div>
                                            <input 
                                                type="checkbox" 
                                                className="hidden" 
                                                checked={filters.subCategories.has(sub)}
                                                onChange={() => toggleSetFilter('subCategories', sub)}
                                            />
                                            <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium truncate">{sub}</span>
                                        </label>
                                    ))}
                                </div>
                          </FilterSection>
                      )}

                      {availableFarmers.length > 0 && (
                          <FilterSection title="Farmers" icon={Tractor}>
                                <div className="mb-2 relative">
                                    <input 
                                        type="text" 
                                        placeholder="Find farmer..." 
                                        value={farmerSearch}
                                        onChange={(e) => setFarmerSearch(e.target.value)}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-8 pr-2 py-1.5 text-xs focus:outline-none focus:border-leaf-400"
                                    />
                                    <Search size={12} className="absolute left-2.5 top-2 text-gray-400" />
                                </div>
                                <div className="space-y-1 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                                    {availableFarmers.map(farmer => (
                                        <label key={farmer.id} className="flex items-center gap-3 cursor-pointer group hover:bg-gray-50 p-2 rounded-lg transition -mx-2">
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition flex-shrink-0 ${filters.farmers.has(farmer.id) ? 'bg-leaf-600 border-leaf-600' : 'border-gray-300'}`}>
                                                {filters.farmers.has(farmer.id) && <Check size={10} className="text-white" />}
                                            </div>
                                            <input 
                                                type="checkbox" 
                                                className="hidden" 
                                                checked={filters.farmers.has(farmer.id)}
                                                onChange={() => toggleSetFilter('farmers', farmer.id)}
                                            />
                                            <span className="text-sm text-gray-600 group-hover:text-gray-900 truncate font-medium">{farmer.farmName}</span>
                                        </label>
                                    ))}
                                </div>
                          </FilterSection>
                      )}

                      <FilterSection title="Quick Filter" icon={Star}>
                        <div className="space-y-1">
                          {[
                            { key: 'organic', label: 'Organic Certified', icon: Leaf, color: 'text-green-600' },
                            { key: 'local', label: 'Locally Sourced', icon: MapPin, color: 'text-blue-600' },
                            { key: 'rating4Plus', label: 'Top Rated (4+)', icon: Star, color: 'text-yellow-600' },
                            { key: 'onSale', label: 'On Sale', icon: Zap, color: 'text-orange-600' },
                            { key: 'inStock', label: 'In Stock', icon: Check, color: 'text-gray-600' }
                          ].map(({ key, label, icon: Icon, color }) => {
                             const isActive = filters[key as keyof typeof filters] as boolean;
                             return (
                                <label key={key} className="flex items-center gap-3 cursor-pointer group hover:bg-gray-50 p-2 rounded-lg transition -mx-2">
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition flex-shrink-0 ${isActive ? 'bg-leaf-600 border-leaf-600' : 'border-gray-300'}`}>
                                        {isActive && <Check size={10} className="text-white" />}
                                    </div>
                                    <input 
                                        type="checkbox" 
                                        className="hidden" 
                                        checked={isActive}
                                        onChange={() => toggleFilter(key as keyof typeof filters)} 
                                    />
                                    <span className={`text-sm group-hover:text-gray-900 font-medium flex items-center gap-2 ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>
                                        <Icon size={14} className={color} fill={key === 'rating4Plus' ? 'currentColor' : 'none'}/> {label}
                                    </span>
                                </label>
                             );
                          })}
                        </div>
                      </FilterSection>

                    </aside>

                    {/* MAIN GRID CONTENT */}
                    <main className="flex-1">
                       {/* Header Bar */}
                       <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                             <span className="font-bold text-gray-900 text-lg">{selectedCategory === 'All' ? 'All Products' : selectedCategory}</span>
                             <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-bold">{filteredProducts.length} items</span>
                          </div>

                          <div className="flex items-center gap-3">
                             <button 
                                onClick={() => setIsMobileFilterOpen(true)}
                                className="lg:hidden flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-bold text-sm transition"
                             >
                                <Filter size={16} /> Filters
                             </button>

                             <div className="relative">
                                <button 
                                  onClick={() => setIsSortOpen(!isSortOpen)}
                                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-leaf-500 text-gray-700 px-4 py-2 rounded-lg font-bold text-sm transition min-w-[180px] justify-between"
                                >
                                  <span className="flex items-center gap-2"><ArrowUpDown size={14} /> 
                                    {sortOption === 'featured' ? 'Sort: Featured' : 
                                     sortOption === 'price-low' ? 'Price: Low to High' : 
                                     sortOption === 'price-high' ? 'Price: High to Low' : 
                                     sortOption === 'rating' ? 'Highest Rated' : 'Name (A-Z)'}
                                  </span>
                                  <ChevronDown size={14} className={`transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {isSortOpen && (
                                  <>
                                    <div className="fixed inset-0 z-10" onClick={() => setIsSortOpen(false)}></div>
                                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-20 py-2 animate-in fade-in zoom-in-95 duration-100">
                                      {[
                                        { label: 'Featured', value: 'featured' },
                                        { label: 'Price: Low to High', value: 'price-low' },
                                        { label: 'Price: High to Low', value: 'price-high' },
                                        { label: 'Highest Rated', value: 'rating' },
                                        { label: 'Name (A-Z)', value: 'name' }
                                      ].map(opt => (
                                        <button
                                          key={opt.value}
                                          onClick={() => { setSortOption(opt.value); setIsSortOpen(false); }}
                                          className={`w-full text-left px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition ${sortOption === opt.value ? 'text-leaf-600 bg-leaf-50' : 'text-gray-700'}`}
                                        >
                                          {opt.label}
                                        </button>
                                      ))}
                                    </div>
                                  </>
                                )}
                             </div>
                          </div>
                       </div>

                       {/* Active Filters */}
                       {activeFilterCount > 0 && (
                          <div className="flex flex-wrap gap-2 mb-6">
                            <span className="text-xs font-bold text-gray-400 mr-2 uppercase tracking-wide self-center">Active:</span>
                            
                            {selectedCategory !== 'All' && (
                              <button onClick={() => handleCategoryChange('All')} className="bg-white border border-leaf-200 text-leaf-700 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 hover:bg-leaf-50 transition shadow-sm">
                                {selectedCategory} <X size={12} />
                              </button>
                            )}
                            {(minPrice > 0 || (maxPrice < maxProductPrice && maxPrice !== 10000)) && (
                              <button onClick={() => { setMinPrice(0); setMaxPrice(maxProductPrice); }} className="bg-white border border-leaf-200 text-leaf-700 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 hover:bg-leaf-50 transition shadow-sm">
                                Price: ₹{minPrice} - ₹{maxPrice} <X size={12} />
                              </button>
                            )}
                            {Object.entries(filters).map(([key, isActive]) => {
                                if (isActive === true) {
                                    return (
                                        <button key={key} onClick={() => toggleFilter(key as keyof typeof filters)} className="bg-white border border-leaf-200 text-leaf-700 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 hover:bg-leaf-50 transition shadow-sm capitalize">
                                            {key.replace(/([A-Z])/g, ' $1').replace('4 Plus', '+').trim()} <X size={12} />
                                        </button>
                                    );
                                }
                                if (key === 'farmers' && (isActive as Set<string>).size > 0) {
                                    return (
                                        <button key={key} onClick={() => setFilters(prev => ({...prev, farmers: new Set()}))} className="bg-white border border-leaf-200 text-leaf-700 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 hover:bg-leaf-50 transition shadow-sm">
                                            Farmers: {(isActive as Set<string>).size} <X size={12} />
                                        </button>
                                    );
                                }
                                if (key === 'subCategories' && (isActive as Set<string>).size > 0) {
                                    return (
                                        <button key={key} onClick={() => setFilters(prev => ({...prev, subCategories: new Set()}))} className="bg-white border border-leaf-200 text-leaf-700 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 hover:bg-leaf-50 transition shadow-sm">
                                            Types: {(isActive as Set<string>).size} <X size={12} />
                                        </button>
                                    );
                                }
                                return null;
                            })}
                            
                            <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 ml-auto hover:underline px-2">
                              Clear All
                            </button>
                          </div>
                       )}

                       {/* Products Grid or Journal */}
                       {filteredProducts.length > 0 ? (
                         <>
                           {viewMode === 'grid' ? (
                             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                                {currentProducts.map(product => (
                                  <ProductCard key={product.id} product={product} highlightTerm={query} />
                                ))}
                             </div>
                           ) : (
                             <div className="space-y-6">
                                {currentProducts.map(product => {
                                  const details = getProductDetails(product);
                                  return (
                                    <div key={product.id} className="group bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row gap-8 items-center">
                                       {/* Image */}
                                       <div className="w-full md:w-48 h-48 shrink-0 bg-gray-50 rounded-2xl overflow-hidden relative cursor-pointer" onClick={() => navigate(`/product/${product.id}`)}>
                                          <img src={getProductImage(product.id, product.image)} alt={product.name.en} className="w-full h-full object-cover mix-blend-multiply p-4 transition-transform duration-500 group-hover:scale-110" />
                                          {product.isOrganic && (
                                            <div className="absolute top-3 left-3 bg-green-100 text-green-800 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider">Organic</div>
                                          )}
                                       </div>

                                       {/* Content */}
                                       <div className="flex-grow space-y-4 text-center md:text-left w-full">
                                          <div>
                                             <div className="flex flex-col md:flex-row md:items-center gap-2 mb-1">
                                                <h3 className="text-2xl font-bold text-gray-900 font-serif cursor-pointer hover:text-leaf-700 transition-colors" onClick={() => navigate(`/product/${product.id}`)}>{product.name.en}</h3>
                                                <div className="flex justify-center md:justify-start gap-2 text-xs text-gray-500">
                                                   <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{product.name.hi}</span>
                                                   <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{product.name.bn}</span>
                                                </div>
                                             </div>
                                             <p className="text-gray-600 leading-relaxed line-clamp-2">{product.description}</p>
                                          </div>

                                          {/* Details Grid */}
                                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-y border-gray-100">
                                             <div className="text-center md:text-left">
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center justify-center md:justify-start gap-1"><MapPin size={10}/> Origin</p>
                                                <p className="text-sm font-bold text-gray-700 truncate">{details.origin}</p>
                                             </div>
                                             <div className="text-center md:text-left">
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center justify-center md:justify-start gap-1"><Sun size={10}/> Harvest</p>
                                                <p className="text-sm font-bold text-gray-700">{details.harvestTime}</p>
                                             </div>
                                             <div className="text-center md:text-left hidden sm:block">
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center justify-center md:justify-start gap-1"><Zap size={10}/> Nutrition</p>
                                                <p className="text-sm font-bold text-gray-700">{details.benefits[0] || 'Nutrition info pending'}</p>
                                             </div>
                                             <div className="text-center md:text-left hidden sm:block">
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center justify-center md:justify-start gap-1"><Star size={10}/> Rating</p>
                                                <p className="text-sm font-bold text-gray-700 flex items-center justify-center md:justify-start gap-1">{details.rating.toFixed(1)} <span className="text-yellow-400">★</span></p>
                                             </div>
                                          </div>
                                       </div>

                                       {/* Action */}
                                       <div className="flex flex-row md:flex-col items-center gap-4 w-full md:w-auto justify-between border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-8">
                                          <div className="text-left md:text-right">
                                             <p className="text-xs text-gray-400 font-medium">Price per {product.baseUnit}</p>
                                             <p className="text-3xl font-extrabold text-gray-900">₹{product.price}</p>
                                          </div>
                                          <button 
                                            onClick={() => addToCart(product)}
                                            className="bg-gray-900 text-white w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shadow-lg hover:bg-leaf-600 hover:scale-110 transition-all active:scale-95"
                                            title="Add to Cart"
                                          >
                                             <ShoppingCart size={20} />
                                          </button>
                                       </div>
                                    </div>
                                  );
                                })}
                             </div>
                           )}

                           {/* Pagination */}
                           {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-3 mt-16 bg-white p-4 rounded-full shadow-sm border border-gray-100 w-fit mx-auto">
                              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="text-sm font-bold text-gray-600 px-2 disabled:opacity-50 hover:text-leaf-600">Prev</button>
                              <span className="text-sm font-bold text-leaf-600 px-2">Page {currentPage} of {totalPages}</span>
                              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="text-sm font-bold text-gray-600 px-2 disabled:opacity-50 hover:text-leaf-600">Next</button>
                            </div>
                          )}
                         </>
                       ) : (
                         <div className="bg-white rounded-[2rem] p-16 text-center border border-gray-100 shadow-sm flex flex-col items-center">
                           <div className="bg-gray-50 w-32 h-32 rounded-full flex items-center justify-center mb-6 relative">
                             <Search size={48} className="text-leaf-300" />
                           </div>
                           <h3 className="text-2xl font-extrabold text-gray-900 mb-3">No harvests found</h3>
                           <p className="text-gray-500 mb-8 max-w-md mx-auto leading-relaxed">
                             We couldn't find any items matching your current filters. Try adjusting the price range or clearing some filters to see more results.
                           </p>
                           <button 
                            onClick={clearFilters}
                            className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3.5 rounded-xl font-bold transition shadow-lg hover:shadow-xl flex items-center gap-2"
                           >
                             <RefreshCw size={18} /> Clear Filters & Show All
                           </button>
                         </div>
                       )}
                    </main>
                  </div>
                )}
            </>
        )}
      </div>

      {/* 5. MOBILE FILTER DRAWER */}
      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileFilterOpen(false)}></div>
          <div className="absolute inset-y-0 right-0 w-full max-w-xs bg-white shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2"><Filter size={18}/> Filters</h3>
              <button onClick={() => setIsMobileFilterOpen(false)} className="p-2 bg-white rounded-full text-gray-500 hover:text-red-500 shadow-sm"><X size={18}/></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5 space-y-8">
               {/* Mobile Categories */}
               <div>
                  <h4 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Categories</h4>
                  <div className="space-y-2">
                    <button onClick={() => handleCategoryChange('All')} className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${selectedCategory === 'All' ? 'bg-leaf-100 text-leaf-800' : 'bg-gray-50 text-gray-600'}`}>All Products</button>
                    {Object.entries(categories).map(([cat, count]) => (
                      <button key={cat} onClick={() => handleCategoryChange(cat)} className={`w-full flex justify-between px-3 py-2 rounded-lg text-sm font-medium ${selectedCategory === cat ? 'bg-leaf-100 text-leaf-800' : 'bg-gray-50 text-gray-600'}`}>
                        <span>{cat}</span> <span className="opacity-60">{count}</span>
                      </button>
                    ))}
                  </div>
               </div>

               {/* Mobile Price */}
               <div>
                  <h4 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Price Range</h4>
                  <input type="range" min="0" max={maxProductPrice} value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none accent-leaf-600" />
                  <div className="flex justify-between mt-2 font-bold text-sm"><span>₹{minPrice}</span><span>₹{maxPrice}</span></div>
               </div>

               {/* Mobile Status */}
               <div>
                  <h4 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Refine By</h4>
                  <div className="space-y-3">
                    {[
                      { key: 'organic', label: 'Organic Certified', icon: Leaf },
                      { key: 'local', label: 'Locally Sourced', icon: MapPin },
                      { key: 'rating4Plus', label: '4★ & Above', icon: Star },
                      { key: 'onSale', label: 'On Sale', icon: Zap },
                      { key: 'inStock', label: 'In Stock Only', icon: Check }
                    ].map(({ key, label, icon: Icon }) => (
                      <label key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-bold text-gray-700 flex items-center gap-2"><Icon size={14}/> {label}</span>
                        <input 
                            type="checkbox" 
                            className="w-5 h-5 accent-leaf-600 rounded" 
                            checked={filters[key as keyof typeof filters] as boolean} 
                            onChange={() => toggleFilter(key as keyof typeof filters)} 
                        />
                      </label>
                    ))}
                  </div>
               </div>
            </div>

            <div className="p-5 border-t border-gray-100 bg-gray-50 flex gap-3">
              <button onClick={clearFilters} className="flex-1 py-3 text-gray-600 font-bold hover:text-red-500 transition">Reset</button>
              <button onClick={() => setIsMobileFilterOpen(false)} className="flex-[2] bg-leaf-600 text-white py-3 rounded-xl font-bold shadow-lg">Apply Filters</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
